import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSendAffiliateEmail } from '@/api/affiliate/mutations/useSendAffiliateEmail'
import { useAffiliateUserData } from '@/api/affiliate/queries/useAffiliateUserData'
import { useCurrentUser } from '@/api/auth/queries/useCurrentUser'
import { Chip } from '@/components/Chip'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'

import { AffiliateCouponsTab } from '../affiliate-tabs/AffiliateCouponsTab'
import { AffiliateCustomersTab } from '../affiliate-tabs/AffiliateCustomersTab'
import { AffiliateOrdersTab } from '../affiliate-tabs/AffiliateOrdersTab'
import { AffiliateProfitsTab } from '../affiliate-tabs/AffiliateProfitsTab'

type AffiliateTabKey = 'coupons' | 'profits' | 'orders' | 'customers'

export default function ProfileAffiliatePage() {
  const { data: user } = useCurrentUser()
  const affiliateId = user?.affiliateId
  const { data: refUserData, isLoading: isLoadingAffiliateUserData } = useAffiliateUserData(affiliateId)
  const { mutate: sendAffiliateEmail, isPending: isSendingEmail } = useSendAffiliateEmail()

  const [isAffiliateModalOpen, setIsAffiliateModalOpen] = useState(false)
  const [affiliateModalMessage, setAffiliateModalMessage] = useState('')
  const [activeTab, setActiveTab] = useState<AffiliateTabKey>('coupons')
  const formAboutRef = useRef<HTMLTextAreaElement>(null)
  const formLinksRef = useRef<HTMLTextAreaElement>(null)

  const { t } = useTranslation()
  const handleAffiliateSubmit = async () => {
    if (!formAboutRef.current?.value) return
    if (!user) return

    const data = `name: ${user.name} ${user.familyName}. phone: ${user.phoneNumber}. email: ${user.email}.
    ${t('affiliate.tellUsAboutYourself')} ${formAboutRef.current.value}. ${t('affiliate.socialLinks')}: ${formLinksRef.current?.value || ''}`

    try {
      await sendAffiliateEmail(data)
      setAffiliateModalMessage(t('affiliate.emailSentSuccess'))
      setIsAffiliateModalOpen(true)
      formAboutRef.current.value = ''
      if (formLinksRef.current) formLinksRef.current.value = ''
    } catch {
      setAffiliateModalMessage(t('affiliate.emailErrorTryAgain'))
      setIsAffiliateModalOpen(true)
    }
  }
  if (affiliateId && isLoadingAffiliateUserData) {
    return (
      <Card>
        <CardContent>
          <Spinner className='mx-auto size-10' />
        </CardContent>
      </Card>
    )
  }

  if (affiliateId && refUserData?.userId) {
    const chipTabs: { key: AffiliateTabKey; label: string }[] = [
      { key: 'coupons', label: t('affiliate.tabCoupons') },
      { key: 'profits', label: t('affiliate.tabProfits') },
      { key: 'orders', label: t('affiliate.tabOrders') },
      { key: 'customers', label: t('affiliate.tabCustomers') },
    ]

    const renderActiveTab = () => {
      switch (activeTab) {
        case 'profits':
          return <AffiliateProfitsTab refUserData={refUserData} />
        case 'orders':
          return <AffiliateOrdersTab ordersBreakdown={refUserData.ordersBreakdown ?? []} ordersByBrand={refUserData.ordersByBrand ?? []} />
        case 'customers':
          return (
            <AffiliateCustomersTab
              customerMetrics={
                refUserData.customerMetrics ?? {
                  avgCustomerValue: { current: 0, previous: 0 },
                  returningCustomersRate: { current: 0, previous: 0 },
                  avgOrdersPerCustomer: { current: 0, previous: 0 },
                }
              }
            />
          )
        case 'coupons':
        default:
          return <AffiliateCouponsTab refUserData={refUserData} />
      }
    }

    return (
      <>
        <div className='space-y-4 md:space-y-6'>
          <div className='flex flex-wrap justify-start gap-2'>
            {chipTabs.map(tab => (
              <Chip key={tab.key} selected={activeTab === tab.key} onSelect={() => setActiveTab(tab.key)}>
                {tab.label}
              </Chip>
            ))}
          </div>
          {renderActiveTab()}
        </div>

        {/* Affiliate Modal */}
        {isAffiliateModalOpen && (
          <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50' dir='rtl'>
            <div className='mx-4 w-full max-w-md rounded-lg bg-white p-6'>
              <h3 className='mb-4 text-xl font-medium'>{t('affiliate.attention')}</h3>
              <p className='text-text-secondary mb-4'>{affiliateModalMessage}</p>
              <Button onClick={() => setIsAffiliateModalOpen(false)} className='w-full'>
                {t('common.close')}
              </Button>
            </div>
          </div>
        )}
      </>
    )
  }

  // No affiliate account - show application form
  return (
    <>
      <Card className='bg-white'>
        <CardHeader>
          <CardTitle className='text-text-primary text-xl font-medium'>{t('affiliate.title')}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='text-text-primary py-4 text-center'>{t('affiliate.wantToPartner')}</div>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='about'>{t('affiliate.tellUsAboutYourself')}</Label>
              <textarea
                ref={formAboutRef}
                id='about'
                rows={4}
                className='border-border-default w-full rounded-lg border p-3 text-sm'
                dir='rtl'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='links'>{t('affiliate.socialLinks')}</Label>
              <textarea
                ref={formLinksRef}
                id='links'
                rows={4}
                className='border-border-default w-full rounded-lg border p-3 text-sm'
                dir='rtl'
              />
            </div>
            <Button onClick={handleAffiliateSubmit} disabled={isSendingEmail} className='w-full'>
              {isSendingEmail ? t('affiliate.sending') : t('affiliate.send')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Affiliate Modal */}
      {isAffiliateModalOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50' dir='rtl'>
          <div className='mx-4 w-full max-w-md rounded-lg bg-white p-6'>
            <h3 className='mb-4 text-xl font-medium'>{t('affiliate.attention')}</h3>
            <p className='text-text-secondary mb-4'>{affiliateModalMessage}</p>
            <Button onClick={() => setIsAffiliateModalOpen(false)} className='w-full'>
              {t('common.close')}
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
